import React, { useEffect, useRef, useState } from "react";
import { jsPDF } from "jspdf";
import Button from "../../shared/Button";
import { applyPlugin } from "jspdf-autotable";
import WorkOrderLayout from "../../layouts/InvoiceLayout/WorkOrder";

applyPlugin(jsPDF);

export const PdfGenerator = (props) => {
  const printRef = useRef();
  let pdf = new jsPDF();

  function handleDownloadPdf(output) {
    if (!pdf) {
      pdf = new jsPDF();
    }
    props.createHeader && props.createHeader(pdf);

    // TOTALS (only if exists)
    const totalsElement = printRef.current.querySelector("#totals");
    let hasTotals = false;
    let shouldBreakAfterTotals = false;
    if (totalsElement) {
      hasTotals = true;
      pdf.autoTable({
        setFont: ("Times", "normal"),
        startY: props.startY,
        startX: props.startX,
        theme: "plain",
        styles: { font: "Times" },
        useCss: true,
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          lineWidth: 0.05,
          lineColor: [255, 255, 255],
          fontSize: 8,
        },
        html: totalsElement,
        margin: { top: 4, left: 4, right: 4, bottom: 4 },
      });

      // Force page break after totals - add new page (don't redraw header)
      // Default to true if breakAfterTotals prop is not explicitly set to false
      shouldBreakAfterTotals = props.breakAfterTotals !== false;
      if (shouldBreakAfterTotals) {
        pdf.addPage();
        // Don't redraw header - header should only be on first page
      }
    }

    // pdf.autoTable({
    //   startY: pdf.lastAutoTable.finalY + 33,
    //   startX: props.startX,
    //   html: printRef.current.querySelector(`#work-order`),
    //   setText: "Times",
    //   margin: { top: 4, left: 4, right: 4, bottom: 10 },
    //   styles: { ...(props.headStyles || {}), font: "Times", overflow: "" },
    //   useCss: true,
    //   columnStyles: props.columnStyles,
    //   headStyles: {
    //     ...(props.headStyles || {}),
    //     fillColor: [255, 255, 255],
    //     textColor: [0, 0, 0],
    //     lineWidth: 0.05,
    //     lineColor: [255, 255, 255],
    //     fontSize: 8,
    //     whiteSpace: "nowrap",
    //   },
    // });

    // Optional programmatic sections above the row table: drawBeforeData(pdf, startY) => endY
    let clientSummaryEndY = null;
    if (typeof props.drawBeforeData === "function") {
      const beforeStartY = shouldBreakAfterTotals ? 10 : props.startY;
      const endY = props.drawBeforeData(pdf, beforeStartY);
      // The data table adds its own gap; keep the title tight against it
      if (typeof endY === "number") clientSummaryEndY = endY - 6;
    }

    // Per-client summary (HTML table), drawn above the row table when present.
    const preTableSummaryIds = ["multi-client-totals"];
    preTableSummaryIds.forEach((summaryId) => {
      const summaryElement = printRef.current.querySelector(`#${summaryId}`);
      if (!summaryElement) return;
      const summaryStartY = clientSummaryEndY
        ? clientSummaryEndY + 6
        : shouldBreakAfterTotals
        ? 10
        : props.startY;
      pdf.autoTable({
        startY: summaryStartY,
        startX: props.startX,
        html: summaryElement,
        styles: { font: "Times", fontSize: summaryId === "invoice-analytics" ? 7 : 8 },
        useCss: true,
        headStyles: {
          fillColor: [241, 196, 15],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          fontSize: 8,
        },
        margin: { top: 4, left: 4, right: 4, bottom: 4 },
      });
      clientSummaryEndY = pdf.lastAutoTable ? pdf.lastAutoTable.finalY : null;
    });

    // Main data table
    const dataTableElement = printRef.current.querySelector(`#${props.id}`);
    if (!dataTableElement) {
      console.warn(`Table element #${props.id} not found`);
    }

    // Check if grouped summary exists - if so, reduce bottom margin to minimize gap
    const groupedSummaryElement = printRef.current.querySelector(
      "#grouped-paydate-summary"
    );
    const hasGroupedSummary = !!groupedSummaryElement;
    const dataTableBottomMargin = hasGroupedSummary ? 5 : 10; // Small bottom margin (5mm) if grouped summary follows

    // If we broke to a new page after totals, start near the top (no header on subsequent pages)
    // Otherwise, continue from where the totals table ended
    const startYForDataTable = clientSummaryEndY
      ? clientSummaryEndY + 6
      : shouldBreakAfterTotals
      ? 10 // Start near top of new page (no header, so no need for header space)
      : pdf.lastAutoTable
      ? pdf.lastAutoTable.finalY + (hasTotals ? 0 : 0)
      : props.startY;

    pdf.autoTable({
      startY: startYForDataTable,
      startX: props.startX,
      html: dataTableElement,
      setText: "Times",
      margin: { top: 4, left: 4, right: 4, bottom: dataTableBottomMargin },
      styles: { ...(props.headStyles || {}), font: "Times", overflow: "" },
      useCss: true,
      columnStyles: props.columnStyles,
      headStyles: {
        ...(props.headStyles || {}),
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        lineWidth: 0.05,
        lineColor: [255, 255, 255],
        fontSize: 8,
        whiteSpace: "nowrap",
      },
      rowPageBreak: props.rowPageBreak,
      didDrawCell: props.didDrawCell,
      willDrawCell: props.willDrawCell,
      didParseCell: props.didParseCell,
      // didDrawPage: props.didDrawPage,
      // Report-specific overrides (e.g. themed invoice rows)
      ...(props.dataTableOptions || {}),
    });

    // Optional programmatic sections after the row table: drawAfterData(pdf, startY)
    if (typeof props.drawAfterData === "function" && pdf.lastAutoTable) {
      props.drawAfterData(pdf, pdf.lastAutoTable.finalY + 8);
    }

    // TOTALS at bottom (only if exists and we have a previous table)
    const totalsElementBottom = printRef.current.querySelector("#totals");
    if (totalsElementBottom && pdf.lastAutoTable) {
      pdf.autoTable({
        startY: pdf.lastAutoTable.finalY + 13,
        startX: props.startX,
        theme: "plain",
        styles: { font: "Times" },
        useCss: true,
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          lineWidth: 0.05,
          lineColor: [255, 255, 255],
          fontSize: 8,
        },
        html: totalsElementBottom,
        margin: { top: 4, left: 4, right: 4, bottom: 4 },
      });
    }

    // Grouped PayDate Summary at bottom (for grouped payout reports)
    if (groupedSummaryElement && pdf.lastAutoTable) {
      // Always start on a new page to prevent overlap
      pdf.addPage();
      const headerY = 5; // Start at the very top of new page (no top margin)

      // Add header text before the table
      const headerElement = printRef.current.querySelector(".grouped-paydate-header");
      if (headerElement) {
        pdf.setFontSize(12); // Increased from 10 to make it bigger
        pdf.setFont("Times", "bold");
        pdf.text("Grouped by Pay Date", props.startX || 4, headerY);
      }

      // Calculate column widths for grouped summary table (9 columns)
      // Page width is typically 210mm, with margins we have ~202mm available
      const pageWidth = pdf.internal.pageSize.width;
      const availableWidth = pageWidth - (props.startX || 4) * 2;
      const columnStyles = {
        0: { cellWidth: availableWidth * 0.11 }, // Pay Date
        1: { cellWidth: availableWidth * 0.14 }, // Manifest Dates
        2: { cellWidth: availableWidth * 0.08 }, // Payouts
        3: { cellWidth: availableWidth * 0.09 }, // Mileage
        4: { cellWidth: availableWidth * 0.08 }, // Hours
        5: { cellWidth: availableWidth * 0.11 }, // Mileage Total
        6: { cellWidth: availableWidth * 0.11 }, // Hourly Total
        7: { cellWidth: availableWidth * 0.13 }, // Supplemental (increased to fit on one line)
        8: { cellWidth: availableWidth * 0.15 }, // Total Amount
      };

      pdf.autoTable({
        startY: headerY + 6, // Start table after header text (accounting for larger font size)
        startX: props.startX,
        html: groupedSummaryElement,
        setText: "Times",
        margin: { top: 4, left: 4, right: 4, bottom: 10 },
        styles: { ...(props.headStyles || {}), font: "Times", overflow: "linebreak", cellWidth: "wrap" },
        useCss: true,
        columnStyles: columnStyles,
        headStyles: {
          ...(props.headStyles || {}),
          fillColor: [241, 196, 15], // Yellow header like other summary tables
          textColor: [0, 0, 0],
          lineWidth: 0.05,
          lineColor: [255, 255, 255],
          fontSize: 8,
          cellPadding: 2,
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [0, 0, 0],
        },
        alternateRowStyles: {
          fillColor: [249, 250, 251],
        },
        rowPageBreak: "avoid", // Try to keep rows together
        didParseCell: (data) => {
          // Style section rows (header rows)
          if (data.row.raw.type === "section") {
            data.cell.styles.fillColor = [241, 196, 15]; // Yellow background
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fontSize = 8;
          }
          // Prevent header text wrapping, especially for "Supplemental"
          if (data.section === "head") {
            data.cell.styles.overflow = "hidden";
            data.cell.styles.textColor = [0, 0, 0];
          }
        },
      });
    }

    const pages = pdf.internal.getNumberOfPages();
    const pageWidth = pdf.internal.pageSize.width;
    const pageHeight = pdf.internal.pageSize.height;
    pdf.setFontSize(10);

    for (let j = 1; j < pages + 1; j++) {
      let horizontalPos = 5;
      let verticalPos = pageHeight - 5;
      pdf.setPage(j);
      pdf.text(
        `Page ${j} of ${pages}   ${props.pageData}`,
        horizontalPos,
        verticalPos,
        {
          align: "left",
        }
      );
    }

    return output
      ? pdf
      : pdf.save(props.fileName, { returnPromise: true }).then((i) => {
          pdf = null;
        });
  }

  function getPdfOutput() {
    const pdf = handleDownloadPdf(true);
    const pageCount = addFooters();
    const output = pdf.output();
    return output;
  }

  /**
   * Returns the generated PDF as a base64 string (no data-uri prefix).
   * This is useful for emailing PDFs that are generated client-side with jsPDF
   * (ex: trucking invoices) without switching to the server-side PDF generator.
   *
   * @returns {string} base64-encoded PDF
   */
  function getPdfBase64() {
    const doc = handleDownloadPdf(true);
    addFooters();
    const dataUriString = doc.output("datauristring");
    // data:application/pdf;filename=generated.pdf;base64,....
    const base64 = (dataUriString || "").split(",")[1] || "";
    return base64;
  }

  function addFooters() {
    const pageCount = pdf.internal.getNumberOfPages();
    for (var i = 1; i <= pageCount; i++) {
      pdf.text(String(i), 196, 285);
    }
  }

  const childrenWithProps = React.Children.map(props.children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        handleDownloadPdf,
        getPdfOutput,
        getPdfBase64,
        addFooters,
      });
    }
    return child;
  });

  return (
    <>
      {childrenWithProps}
      <div className="hidden" ref={printRef}>
        {props.layout}
      </div>
    </>
  );
};

export default PdfGenerator;
